import React, { useMemo, ReactNode, createContext, useContext } from "react";

import { useAPI } from "../hooks";

import { useActiveFrontendAddons } from "@/components/AppProvider/AppFrontendAddonProvider";
import { ViewMasterRaw } from "@/domain/ViewMaster";

type AvailableViewType = {
  view_type: string;
  type: "viewType" | "topNavButtons" | "extraPage";
  name: string;
  sourceAddon: { id: string; displayName: string } | null;
};

const viewTypesCtx = createContext<{
  availableViewTypes: AvailableViewType[] | null;
  runReloadAvailableViewTypes: () => Promise<void>;
  isLoadedAvailableViewTypes: boolean;
}>({
  availableViewTypes: [],
  runReloadAvailableViewTypes: async () => {
    // nop
  },
  isLoadedAvailableViewTypes: false,
});

// 組織ごとに利用可能なビュータイプを提供するプロバイダ
export const ViewTypesProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const {
    state: availableInternalViewTypes,
    runReloadState: runReloadAvailableInternalViewTypes,
    isLoadedState: isLoadedAvailableInternalViewTypes,
  } = useAPI<ViewMasterRaw[]>("view_masters");
  const { addons, activeAddonIds } = useActiveFrontendAddons();
  const availableAddonViewTypes = useMemo<AvailableViewType[]>(
    () =>
      activeAddonIds.flatMap((addonId) => {
        const addon = addons[addonId];
        if (!addon) return [];
        return addon.viewTypes.map((vt) => ({
          type: "type" in vt ? vt.type : "viewType",
          view_type: vt.key,
          name: vt.displayName,
          sourceAddon: { id: addon.id, displayName: addon.displayName },
        }));
      }),
    [activeAddonIds, addons]
  );
  const viewTypesCtxValue = useMemo(
    () => ({
      availableViewTypes: availableInternalViewTypes
        ? [
            ...availableInternalViewTypes.map((vm) => ({
              view_type: vm.view_type,
              type: "viewType" as const,
              name: vm.name,
              sourceAddon: null,
            })),
            ...availableAddonViewTypes,
          ]
        : null,
      runReloadAvailableViewTypes: runReloadAvailableInternalViewTypes,
      isLoadedAvailableViewTypes: isLoadedAvailableInternalViewTypes,
    }),
    [
      availableAddonViewTypes,
      availableInternalViewTypes,
      runReloadAvailableInternalViewTypes,
      isLoadedAvailableInternalViewTypes,
    ]
  );

  return (
    <viewTypesCtx.Provider value={viewTypesCtxValue}>
      {children}
    </viewTypesCtx.Provider>
  );
};

export const useAvailableViewTypes = () => {
  return useContext(viewTypesCtx);
};
